import { createFileRoute } from '@tanstack/react-router'
import { AlertCircle, Lock } from 'lucide-react'
import { NotFound } from '@/components/custom/not-found'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { CompanyDetailLayout } from '@/components/custom/companies/overview/company-detail-layout'
import { isOgsmEnabled } from '@/configs/feature-flags'
import {
  OGSM_CAPABILITY,
  OGSM_INTERNAL_EDIT_CAPABILITY,
} from '@/features/ogsm/access'
import { parseOgsmSearch } from '@/features/ogsm/search'
import { OgsmPageHeader } from '@/components/custom/ogsm/ogsm-page-header'
import { OgsmWorkspace } from '@/components/custom/ogsm/ogsm-workspace'
import { useCompany } from '@/hooks/use-companies'
import {
  selectCapabilities,
  selectIsProfileReady,
  useUserStore,
} from '@/stores/user.store'

/**
 * The OGSM screen for one company: `/companies/:companyId/ogsm`.
 *
 * The trailing underscore on `$companyId_` keeps this route out from under the
 * company detail page, which has no outlet to render a child in.
 */
export const Route = createFileRoute(
  '/_authenticated/companies/$companyId_/ogsm'
)({
  validateSearch: parseOgsmSearch,
  component: OgsmRoute,
})

// `notFound()` in `beforeLoad` leaves this nested route blank, so render it.
function OgsmRoute() {
  if (!isOgsmEnabled()) return <NotFound className="h-full" />
  return <OgsmScreen />
}

// Not in `beforeLoad`: the profile isn't loaded yet there, so a cold load would be denied.
function OgsmScreen() {
  const { companyId } = Route.useParams()
  const { session, objective } = Route.useSearch()
  const navigate = Route.useNavigate()
  const capabilities = useUserStore(selectCapabilities)
  const isProfileReady = useUserStore(selectIsProfileReady)
  const { data: company, isLoading, isError, refetch } = useCompany(companyId)

  if (!isProfileReady) return null

  if (!capabilities.includes(OGSM_CAPABILITY)) {
    return <OgsmNoAccess />
  }

  if (isLoading) {
    return (
      <CompanyDetailLayout>
        <div className="space-y-4 px-3 pb-8 pt-12 sm:px-4">
          <Skeleton className="h-5 w-60" />
          <Skeleton className="h-12 w-80" />
          <Skeleton className="h-96 w-full rounded-sm" />
        </div>
      </CompanyDetailLayout>
    )
  }

  if (isError || !company) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3 rounded-lg border border-primary/15 bg-white px-8 py-10">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <Typography variant="h5">Failed to load company details</Typography>
          <Button variant="outline" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <CompanyDetailLayout>
      <div className="space-y-4 px-3 pb-8 pt-12 sm:px-4">
        <OgsmPageHeader
          companyId={company.id}
          companyName={company.displayName}
          sessionId={session}
          objectiveId={objective}
        />

        <OgsmWorkspace
          key={company.id}
          companyId={company.id}
          companyName={company.displayName}
          sessionId={session}
          objectiveId={objective}
          onNavigate={(next) =>
            navigate({ search: (prev) => ({ ...prev, ...next }) })
          }
          readOnlyViewer={!capabilities.includes(OGSM_INTERNAL_EDIT_CAPABILITY)}
        />
      </div>
    </CompanyDetailLayout>
  )
}

function OgsmNoAccess() {
  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <NotFound
        useCard
        icon={<Lock strokeWidth={1} className="size-12 text-primary/70" />}
        title="No access to OGSM"
        description="OGSM is available to Greybull users."
        onActionLabel="Go back to home"
        className="w-full max-w-md flex-none gap-5 bg-background p-10 md:p-10"
        titleClassName="text-xl"
      />
    </div>
  )
}
